import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
    Check,
    ChevronDown,
    ChevronRight,
    Heart,
    ListFilter,
    PackageCheck,
    Search,
    ShoppingCart,
    SlidersHorizontal,
    Star,
    Truck,
    X,
    Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { WishlistPurchaseFilter } from "./WishlistData";
import { useWishlist } from "../../../context/WishlistContext";
import { useCart } from "../../../context/CartContext";
import HomeSecondaryNavbar from "../../globalcomponents/HomeSecondaryNavbar/HomeSecondaryNavbar";
import HomeNavbar from "../../globalcomponents/HomeNavbar/HomeNavbar";
import MobileDetailNavbar from "../../globalcomponents/mobile/MobileDetailNavbar/MobileDetailNavbar";
import SideCart from "../../globalcomponents/SideCart/SideCart";
import "./WishlistPage.css";

const PRODUCT_BASE_URL = `${process.env.REACT_APP_API_BASE_URL || ""}${process.env.REACT_APP_ECOMMERCE_PERFIX || ""}`;

type WishlistSort = "recent" | "price-low" | "price-high" | "rating" | "discount";

type WishlistMobileSheet = "departments" | "filters" | null;

type WishlistSelectOption<T extends string> = {
    value: T;
    label: string;
};

type WishlistCustomSelectProps<T extends string> = {
    value: T;
    options: WishlistSelectOption<T>[];
    onChange: (value: T) => void;
    ariaLabel: string;
    className?: string;
};

function WishlistCustomSelect<T extends string>({
    value,
    options,
    onChange,
    ariaLabel,
    className = "",
}: WishlistCustomSelectProps<T>) {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const selectedOption = options.find((option) => option.value === value) || options[0];

    useEffect(() => {
        const handlePointerDown = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handlePointerDown);
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousedown", handlePointerDown);
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    return (
        <div ref={wrapperRef} className={`wishlist-custom-select ${className} ${open ? "open" : ""}`}>
            <button
                type="button"
                className="wishlist-custom-select-trigger"
                onClick={() => setOpen((previous) => !previous)}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label={ariaLabel}
            >
                <span>{selectedOption.label}</span>
                <ChevronDown size={15} strokeWidth={2} />
            </button>
            {open && (
                <div className="wishlist-custom-select-menu" role="listbox" aria-label={ariaLabel}>
                    {options.map((option) => {
                        const selected = option.value === value;
                        return (
                            <button
                                type="button"
                                key={option.value}
                                className={selected ? "selected" : ""}
                                onClick={() => {
                                    onChange(option.value);
                                    setOpen(false);
                                }}
                                role="option"
                                aria-selected={selected}
                            >
                                <span>{option.label}</span>
                                {selected && <Check size={15} strokeWidth={2.4} />}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

const wishlistShowOptions: WishlistSelectOption<WishlistPurchaseFilter>[] = [
    { value: "all", label: "All Items" },
    { value: "purchased", label: "Purchased" },
    { value: "unpurchased", label: "Unpurchased" },
];

const wishlistSortOptions: WishlistSelectOption<WishlistSort>[] = [
    { value: "recent", label: "Recently Added" },
    { value: "rating", label: "Customer Rating" },
    { value: "discount", label: "Discount: High to Low" },
    { value: "price-low", label: "Price: Low to High" },
    { value: "price-high", label: "Price: High to Low" },
];

interface DynamicWishlistProduct {
    id: string | number;
    _id: string;
    ssin?: string;
    badge: string;
    title: string;
    description: string;
    brand: string;
    category: string;
    superCategory: string;
    image: string;
    colors: { name: string; value: string }[];
    extraColors: number;
    rating: number;
    reviews: number;
    deal: string;
    dealType: string;
    price: number;
    mrp: number;
    delivery: string;
    deliveryType: "delivery" | "fast" | "stock";
    sellerId?: string;
    sellerSkuId?: string;
    addedAt: string;
    isPurchased: boolean;
}

const formatPrice = (value: number) =>
    new Intl.NumberFormat("en-IN").format(Math.max(0, Math.round(value)));

const WishlistPage = () => {
    const navigate = useNavigate();
    const { cartItems, cartCount, addToCart } = useCart();
    const {
        wishlist,
        wishlistCount,
        loading: wishlistLoading,
        removeItemFromWishlist,
        fetchWishlist,
    } = useWishlist();

    const [browsingHistoryOpen, setBrowsingHistoryOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [purchaseFilter, setPurchaseFilter] = useState<WishlistPurchaseFilter>("all");
    const [selectedDepartment, setSelectedDepartment] = useState("all");
    const [sort, setSort] = useState<WishlistSort>("recent");
    const [selectedColors, setSelectedColors] = useState<Record<string | number, number>>({});
    const [catalogMap, setCatalogMap] = useState<Map<string, any>>(new Map());
    const [mobileSheet, setMobileSheet] = useState<WishlistMobileSheet>(null);

    useEffect(() => {
        if (!mobileSheet) {
            return;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setMobileSheet(null);
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [mobileSheet]);

    // Fetch catalog products to enrich wishlist items with live price, mrp, colors & stock details
    useEffect(() => {
        let cancelled = false;
        const loadCatalog = async () => {
            try {
                const res = await axios.get(`${PRODUCT_BASE_URL}/e-commerce/catalog/products`, {
                    params: { limit: 48 },
                });
                const prods = res.data?.data?.products || res.data?.products || [];
                if (!cancelled && prods.length > 0) {
                    const map = new Map<string, any>();
                    prods.forEach((p: any) => {
                        if (p._id) map.set(String(p._id), p);
                        if (p.ssin) map.set(String(p.ssin), p);
                    });
                    setCatalogMap(map);
                }
            } catch {
                // Silently fallback if catalog query fails
            }
        };

        loadCatalog();
        return () => {
            cancelled = true;
        };
    }, []);

    // Ensure wishlist is loaded on page mount
    useEffect(() => {
        if (fetchWishlist) {
            fetchWishlist({ page: 1, limit: 50 });
        }
    }, [fetchWishlist]);

    // Merge wishlist items from WishlistContext with live catalog product details
    const wishlistProducts = useMemo<DynamicWishlistProduct[]>(() => {
        if (!wishlist || wishlist.length === 0) return [];

        return wishlist.map((item, idx) => {
            const itemIdStr = String(item._id || item.wishlistItemId || `item-${idx}`);
            const itemSsinStr = item.ssin ? String(item.ssin) : "";
            const catalog = catalogMap.get(itemIdStr) || (itemSsinStr ? catalogMap.get(itemSsinStr) : null);

            const title = catalog?.name || item.name || "Product";
            const image =
                item.mainImage ||
                catalog?.mainImage ||
                catalog?.images?.[0] ||
                "/assets/img/products/product-1.png";

            const brandName =
                (typeof item.brand === "object" ? item.brand?.name : item.brand) ||
                (typeof catalog?.brand === "object" ? catalog.brand?.name : catalog?.brand) ||
                "Brand";

            const categoryName =
                item.categoryHierarchy?.category ||
                catalog?.categoryHierarchy?.category ||
                item.categoryHierarchy?.superCategory ||
                catalog?.categoryHierarchy?.superCategory ||
                "General";

            const superCategoryName =
                item.categoryHierarchy?.superCategory ||
                catalog?.categoryHierarchy?.superCategory ||
                "All";

            const price = Number(
                catalog?.featuredOffer?.finalPrice ??
                catalog?.pricing?.sellingPrice ??
                catalog?.price ??
                1299
            );

            const mrp = Number(
                catalog?.featuredOffer?.mrp ??
                catalog?.pricing?.mrp ??
                catalog?.mrp ??
                Math.round(price * 1.35)
            );

            const discountPercent =
                catalog?.featuredOffer?.discountPercent ??
                (mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0);

            const colors =
                catalog?.colors && catalog.colors.length > 0
                    ? catalog.colors.map((c: any) => ({
                          name: c.name || c.colorName || "Standard",
                          value: c.hexCode || c.code || c.value || "#111827",
                      }))
                    : [{ name: "Standard", value: "#111827" }];

            const rating = Number(
                item.reviews?.averageRating ??
                catalog?.reviews?.averageRating ??
                catalog?.rating ??
                4.5
            );

            const reviews = Number(
                item.reviews?.totalReviews ??
                catalog?.reviews?.totalReviews ??
                catalog?.reviewCount ??
                42
            );

            const isFast = Boolean(catalog?.featuredOffer?.fastDelivery);

            return {
                id: itemIdStr,
                _id: itemIdStr,
                ssin: itemSsinStr,
                badge: discountPercent > 0 ? `${discountPercent}% OFF` : "Wishlisted",
                title,
                description:
                    catalog?.description ||
                    item.note ||
                    (categoryName ? `Explore the best of ${categoryName}` : "Premium quality product"),
                brand: brandName,
                category: categoryName,
                superCategory: superCategoryName,
                image,
                colors,
                extraColors: Math.max(0, colors.length - 3),
                rating,
                reviews,
                deal: "Limited Time Deal",
                dealType: "deal-limited",
                price,
                mrp,
                delivery: isFast ? "Next-day Delivery" : "FREE Delivery",
                deliveryType: isFast ? "fast" : "delivery",
                sellerId: catalog?.featuredOffer?.sellerId,
                sellerSkuId: catalog?.featuredOffer?.sellerSkuId,
                addedAt: item.addedAt || new Date().toISOString(),
                isPurchased: false,
            };
        });
    }, [wishlist, catalogMap]);

    // Dynamic departments extracted from wishlisted items' categories
    const departments = useMemo(() => {
        const set = new Set<string>();
        wishlistProducts.forEach((p) => {
            if (p.category && p.category !== "General") set.add(p.category);
            if (p.superCategory && p.superCategory !== "All" && p.superCategory !== "Products") {
                set.add(p.superCategory);
            }
        });

        const list = [{ id: "all", label: "All Departments" }];
        Array.from(set).forEach((cat) => {
            list.push({
                id: cat.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
                label: cat,
            });
        });
        return list;
    }, [wishlistProducts]);

    // Filter and sort wishlist products
    const filteredProducts = useMemo(() => {
        const query = search.trim().toLowerCase();
        const selectedDeptObj = departments.find((d) => d.id === selectedDepartment);
        const deptLabel = selectedDeptObj?.id === "all" ? "" : selectedDeptObj?.label.toLowerCase();

        const filtered = wishlistProducts.filter((product) => {
            const matchesSearch =
                !query ||
                product.title.toLowerCase().includes(query) ||
                product.description.toLowerCase().includes(query) ||
                product.brand.toLowerCase().includes(query) ||
                product.category.toLowerCase().includes(query);

            const matchesPurchase =
                purchaseFilter === "all" ||
                (purchaseFilter === "purchased" && product.isPurchased) ||
                (purchaseFilter === "unpurchased" && !product.isPurchased);

            const matchesDepartment =
                !deptLabel ||
                product.category.toLowerCase() === deptLabel ||
                product.superCategory.toLowerCase() === deptLabel;

            return matchesSearch && matchesPurchase && matchesDepartment;
        });

        return [...filtered].sort((first, second) => {
            const firstDiscount = first.mrp > first.price ? ((first.mrp - first.price) / first.mrp) * 100 : 0;
            const secondDiscount = second.mrp > second.price ? ((second.mrp - second.price) / second.mrp) * 100 : 0;

            if (sort === "price-low") return first.price - second.price;
            if (sort === "price-high") return second.price - first.price;
            if (sort === "rating") return second.rating - first.rating;
            if (sort === "discount") return secondDiscount - firstDiscount;

            // Default: recently added
            return new Date(second.addedAt).getTime() - new Date(first.addedAt).getTime();
        });
    }, [wishlistProducts, search, purchaseFilter, selectedDepartment, sort, departments]);

    const selectColor = (productId: string | number, colorIndex: number) => {
        setSelectedColors((previous) => ({ ...previous, [productId]: colorIndex }));
    };

    const handleRemoveFromWishlist = (e: React.MouseEvent, productId: string | number) => {
        e.stopPropagation();
        if (removeItemFromWishlist) {
            removeItemFromWishlist(String(productId));
        }
    };

    const handleProductClick = (product: DynamicWishlistProduct) => {
        const targetId = product._id || product.id || product.ssin;
        navigate(`/DetailPage/${targetId}`, {
            state: {
                product: {
                    ...product,
                    productId: targetId,
                    breadcrumb: ["Home", "Wishlist", product.category, product.title],
                },
            },
        });
    };

    const handleAddToCart = (e: React.MouseEvent, product: DynamicWishlistProduct) => {
        e.stopPropagation();
        const isAdded = cartItems.some((item: any) => String(item.productId) === String(product.id));
        if (isAdded) return;

        const selectedColor = product.colors[selectedColors[product.id] ?? 0];
        addToCart({
            productId: String(product.id),
            title: product.title,
            description: product.description,
            image: product.image,
            price: product.price,
            mrp: product.mrp,
            color: selectedColor?.name,
            variant: selectedColor?.name,
            sellerId: product.sellerId,
            sellerSkuId: product.sellerSkuId,
        });
    };

    return (
        <>
            <div className="wishlist-desktop-navbar">
                <HomeNavbar variant="home" />
            </div>
            <div className="wishlist-mobile-navbar">
                <MobileDetailNavbar cartCount={cartCount} searchPlaceholder="Search Salar" fallbackPath="/HomePage" />
            </div>
            <div className="wishlist-desktop-secondary-navbar">
                <HomeSecondaryNavbar
                    variant="detail"
                    browsingHistoryOpen={browsingHistoryOpen}
                    onBrowsingHistoryClick={() => setBrowsingHistoryOpen((current) => !current)}
                />
            </div>
            <SideCart />
            <main className="wishlist-page">
                <div className="wishlist-shell">
                    <div className="wishlist-breadcrumb">
                        <button type="button" onClick={() => navigate("/HomePage")}>
                            Home
                        </button>
                        <ChevronRight size={15} />
                        <strong>Wishlist</strong>
                    </div>

                    <section className="wishlist-hero">
                        <div>
                            <span>YOUR SAVED PRODUCTS</span>
                            <h1>My Wishlist</h1>
                            <p>
                                Keep your favourite products in one place, compare them easily and move
                                them to your cart whenever you're ready.
                            </p>
                        </div>
                        <div className="wishlist-summary">
                            <span>ITEMS SAVED</span>
                            <strong>{wishlistCount || wishlistProducts.length}</strong>
                            <small>Products in your wishlist</small>
                        </div>
                    </section>

                    <div className="wishlist-content">
                        <aside className="wishlist-filter-panel">
                            <div className="wishlist-filter-head">
                                <strong>Departments</strong>
                            </div>
                            <div className="wishlist-department-list">
                                {departments.map((department) => (
                                    <label
                                        key={department.id}
                                        className={`wishlist-department-option ${
                                            selectedDepartment === department.id ? "active" : ""
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="wishlist-department"
                                            checked={selectedDepartment === department.id}
                                            onChange={() => setSelectedDepartment(department.id)}
                                        />
                                        <span>{department.label}</span>
                                    </label>
                                ))}
                            </div>
                        </aside>

                        <section className="wishlist-results">
                            <div className="wishlist-results-head">
                                <div className="wishlist-results-title">
                                    <span>YOUR SAVED ITEMS</span>
                                    <h2>
                                        {departments.find((item) => item.id === selectedDepartment)?.label ||
                                            "All Departments"}
                                    </h2>
                                    <p>{filteredProducts.length} products available</p>
                                </div>
                                <div className="wishlist-results-controls">
                                    <label className="wishlist-search">
                                        <Search size={19} />
                                        <input
                                            type="search"
                                            value={search}
                                            onChange={(event) => setSearch(event.target.value)}
                                            placeholder="Search item"
                                        />
                                    </label>
                                    <label className="wishlist-select-control">
                                        <span>Show</span>
                                        <div>
                                            <select
                                                value={purchaseFilter}
                                                onChange={(event) =>
                                                    setPurchaseFilter(
                                                        event.target.value as WishlistPurchaseFilter
                                                    )
                                                }
                                            >
                                                <option value="all">All Items</option>
                                                <option value="purchased">Purchased</option>
                                                <option value="unpurchased">Unpurchased</option>
                                            </select>
                                            <ChevronDown size={15} />
                                        </div>
                                    </label>
                                    <label className="wishlist-select-control">
                                        <span>Sort by</span>
                                        <div>
                                            <select
                                                value={sort}
                                                onChange={(event) =>
                                                    setSort(event.target.value as WishlistSort)
                                                }
                                            >
                                                <option value="recent">Recently Added</option>
                                                <option value="rating">Customer Rating</option>
                                                <option value="discount">Discount: High to Low</option>
                                                <option value="price-low">Price: Low to High</option>
                                                <option value="price-high">Price: High to Low</option>
                                            </select>
                                            <ChevronDown size={15} />
                                        </div>
                                    </label>
                                </div>
                            </div>

                            <div className="wishlist-mobile-action-bar">
                                <button type="button" onClick={() => setMobileSheet("departments")}>
                                    <ListFilter size={17} strokeWidth={2} />
                                    <span>Departments</span>
                                    <strong>
                                        {departments.find((item) => item.id === selectedDepartment)?.label || "All Departments"}
                                    </strong>
                                </button>
                                <button type="button" onClick={() => setMobileSheet("filters")}>
                                    <SlidersHorizontal size={17} strokeWidth={2} />
                                    <span>Filters</span>
                                </button>
                            </div>

                            {wishlistLoading && wishlistProducts.length === 0 ? (
                                <div className="wishlist-product-grid">
                                    {Array.from({ length: 4 }).map((_, i) => (
                                        <div
                                            key={i}
                                            style={{
                                                height: 480,
                                                borderRadius: 16,
                                                background: "#eaedf2",
                                                animation: "pulse 1.5s infinite",
                                            }}
                                        />
                                    ))}
                                </div>
                            ) : filteredProducts.length > 0 ? (
                                <div className="wishlist-product-grid">
                                    {filteredProducts.map((product) => {
                                        const discount =
                                            product.mrp > product.price
                                                ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
                                                : 0;
                                        const selectedColor = selectedColors[product.id] ?? 0;
                                        const isAdded = cartItems.some(
                                            (item: any) => String(item.productId) === String(product.id)
                                        );

                                        return (
                                            <article
                                                className="men-product-card"
                                                key={product.id}
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => handleProductClick(product)}
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter" || event.key === " ") {
                                                        event.preventDefault();
                                                        handleProductClick(product);
                                                    }
                                                }}
                                            >
                                                <div className="men-product-image-wrapper">
                                                    <span className="men-product-badge">{product.badge}</span>
                                                    <button
                                                        type="button"
                                                        className="men-product-wishlist active"
                                                        onClick={(e) => handleRemoveFromWishlist(e, product.id)}
                                                        aria-label="Remove from wishlist"
                                                    >
                                                        <Heart size={18} strokeWidth={2} fill="currentColor" />
                                                    </button>
                                                    <img
                                                        src={product.image}
                                                        alt={product.title}
                                                        className="men-product-image"
                                                        onError={(e) => {
                                                            e.currentTarget.src =
                                                                "/assets/images/logos/salarlogo.png";
                                                        }}
                                                    />
                                                </div>

                                                <div className="men-product-info">
                                                    <div className="men-product-colors">
                                                        <div className="men-product-color-list">
                                                            {product.colors.map((color, index) => (
                                                                <button
                                                                    type="button"
                                                                    key={color.name + index}
                                                                    className={`men-product-color ${
                                                                        selectedColor === index ? "active" : ""
                                                                    }`}
                                                                    title={color.name}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        selectColor(product.id, index);
                                                                    }}
                                                                >
                                                                    <span style={{ backgroundColor: color.value }} />
                                                                </button>
                                                            ))}
                                                        </div>
                                                        {product.extraColors > 0 && (
                                                            <button
                                                                type="button"
                                                                className="men-product-more-colors"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleProductClick(product);
                                                                }}
                                                            >
                                                                +{product.extraColors} more
                                                            </button>
                                                        )}
                                                    </div>

                                                    <h3 title={product.title}>{product.title}</h3>
                                                    <p className="men-product-description" title={product.description}>
                                                        {product.description}
                                                    </p>

                                                    <div className="men-product-rating">
                                                        <strong>{product.rating.toFixed(1)}</strong>
                                                        <span className="men-product-stars">
                                                            {[1, 2, 3, 4, 5].map((star) => (
                                                                <Star
                                                                    key={star}
                                                                    size={13}
                                                                    strokeWidth={1.8}
                                                                    fill={
                                                                        star <= Math.round(product.rating)
                                                                            ? "currentColor"
                                                                            : "none"
                                                                    }
                                                                />
                                                            ))}
                                                        </span>
                                                        <span className="men-product-review-count">
                                                            ({product.reviews})
                                                        </span>
                                                    </div>

                                                    <div className={`men-product-deal ${product.dealType}`}>
                                                        <span className="men-product-deal-dot" />
                                                        <strong>{product.deal}</strong>
                                                    </div>

                                                    <div className="men-product-pricing">
                                                        <div className="men-product-price-main">
                                                            <strong>
                                                                <sup>₹</sup>
                                                                {formatPrice(product.price)}
                                                            </strong>
                                                            {discount > 0 && (
                                                                <span className="men-product-discount">
                                                                    {discount}% OFF
                                                                </span>
                                                            )}
                                                        </div>
                                                        {product.mrp > product.price && (
                                                            <div className="men-product-price-secondary">
                                                                <span>MRP</span>
                                                                <del>₹{formatPrice(product.mrp)}</del>
                                                                <span className="men-product-saving">
                                                                    Save ₹{formatPrice(product.mrp - product.price)}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className={`men-product-delivery ${product.deliveryType}`}>
                                                        {product.deliveryType === "delivery" && (
                                                            <Truck size={15} strokeWidth={1.9} />
                                                        )}
                                                        {product.deliveryType === "fast" && (
                                                            <Zap size={15} strokeWidth={2} />
                                                        )}
                                                        {product.deliveryType === "stock" && (
                                                            <PackageCheck size={15} strokeWidth={1.9} />
                                                        )}
                                                        <span>{product.delivery}</span>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className={`men-product-options ${isAdded ? "added" : ""}`}
                                                        onClick={(e) => handleAddToCart(e, product)}
                                                    >
                                                        {isAdded ? (
                                                            <>
                                                                <Check size={16} strokeWidth={2.4} /> Added to Cart
                                                            </>
                                                        ) : (
                                                            <>
                                                                <ShoppingCart size={16} strokeWidth={2} /> Add to Cart
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </article>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="wishlist-empty">
                                    <Heart size={34} />
                                    <h3>
                                        {wishlistProducts.length === 0
                                            ? "Your Wishlist is Empty"
                                            : "No wishlist items found"}
                                    </h3>
                                    <p>
                                        {wishlistProducts.length === 0
                                            ? "Explore our products and save your favorite items here."
                                            : "Try another department, purchase status or search term."}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (wishlistProducts.length === 0) {
                                                navigate("/HomePage");
                                            } else {
                                                setSearch("");
                                                setPurchaseFilter("all");
                                                setSelectedDepartment("all");
                                            }
                                        }}
                                    >
                                        {wishlistProducts.length === 0
                                            ? "Explore Products"
                                            : "Show All Items"}
                                    </button>
                                </div>
                            )}
                        </section>
                    </div>
                </div>
            </main>

            {mobileSheet && (
                <div
                    className="wishlist-mobile-sheet-layer"
                    role="presentation"
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) {
                            setMobileSheet(null);
                        }
                    }}
                >
                    <section
                        className="wishlist-mobile-sheet"
                        role="dialog"
                        aria-modal="true"
                        aria-label={mobileSheet === "departments" ? "Select department" : "Wishlist filters"}
                    >
                        <div className="wishlist-mobile-sheet-handle" />
                        <div className="wishlist-mobile-sheet-header">
                            <div>
                                <span>{mobileSheet === "departments" ? "SHOP BY" : "REFINE ITEMS"}</span>
                                <h2>{mobileSheet === "departments" ? "Departments" : "Filters"}</h2>
                            </div>
                            <button
                                type="button"
                                className="wishlist-mobile-sheet-close"
                                onClick={() => setMobileSheet(null)}
                                aria-label="Close"
                            >
                                <X size={19} strokeWidth={2} />
                            </button>
                        </div>
                        <div
                            className={`wishlist-mobile-sheet-body ${
                                mobileSheet === "filters" ? "wishlist-mobile-sheet-body-filters" : ""
                            }`}
                        >
                            {mobileSheet === "departments" ? (
                                <div className="wishlist-mobile-department-list">
                                    {departments.map((department) => (
                                        <label
                                            key={department.id}
                                            className={`wishlist-mobile-department-option ${
                                                selectedDepartment === department.id ? "active" : ""
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="wishlist-mobile-department"
                                                checked={selectedDepartment === department.id}
                                                onChange={() => {
                                                    setSelectedDepartment(department.id);
                                                    setMobileSheet(null);
                                                }}
                                            />
                                            <span>{department.label}</span>
                                            {selectedDepartment === department.id && (
                                                <Check size={16} strokeWidth={2.4} />
                                            )}
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <div className="wishlist-mobile-filter-form">
                                    <label className="wishlist-mobile-filter-field wishlist-mobile-filter-search">
                                        <span>Search</span>
                                        <div>
                                            <Search size={17} strokeWidth={2} />
                                            <input
                                                type="search"
                                                value={search}
                                                onChange={(event) => setSearch(event.target.value)}
                                                placeholder="Search wishlist items"
                                            />
                                        </div>
                                    </label>
                                    <div className="wishlist-mobile-filter-grid">
                                        <label className="wishlist-mobile-filter-field">
                                            <span>Show</span>
                                            <WishlistCustomSelect
                                                value={purchaseFilter}
                                                options={wishlistShowOptions}
                                                onChange={setPurchaseFilter}
                                                ariaLabel="Show wishlist items"
                                                className="wishlist-custom-select-mobile"
                                            />
                                        </label>
                                        <label className="wishlist-mobile-filter-field">
                                            <span>Sort by</span>
                                            <WishlistCustomSelect
                                                value={sort}
                                                options={wishlistSortOptions}
                                                onChange={setSort}
                                                ariaLabel="Sort wishlist items"
                                                className="wishlist-custom-select-mobile"
                                            />
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="wishlist-mobile-sheet-footer">
                            <button
                                type="button"
                                className="wishlist-mobile-sheet-secondary"
                                onClick={() => {
                                    if (mobileSheet === "departments") {
                                        setSelectedDepartment("all");
                                    } else {
                                        setSearch("");
                                        setPurchaseFilter("all");
                                        setSort("recent");
                                    }
                                }}
                            >
                                Reset
                            </button>
                            <button
                                type="button"
                                className="wishlist-mobile-sheet-primary"
                                onClick={() => setMobileSheet(null)}
                            >
                                Apply
                            </button>
                        </div>
                    </section>
                </div>
            )}
        </>
    );
};

export default WishlistPage;
